/**
 * Modal controller — handles modal DOM wiring, mode switching, and UI helpers.
 *
 * Stateless extraction from ForgeTryonElement so the widget class stays
 * focused on orchestration. All functions accept the DOM elements and
 * callbacks they need, keeping coupling explicit.
 */

import type { ModalElements } from '@mirrably/core/ui';

/** Callbacks the modal wiring can invoke on the widget. */
export interface ModalCallbacks {
  /** Called when the user clicks the snapshot button. */
  onSnapshot: () => void;
  /** Called when the user switches camera. */
  onSwitchCamera: () => void;
  /** Called when the user switches try-on mode (mode !== current). */
  onSwitchMode: (mode: 'live' | 'photo') => void;
  /** Called when a photo file is selected via input or drag-drop. */
  onPhotoFile: (file: File) => void;
  /** Called when add-to-cart is clicked. Receives the modal for button feedback. */
  onAddToCart: () => void;
}

/**
 * Wire up all button handlers on the modal elements.
 *
 * This is the main extraction point: instead of 60+ lines of addEventListener
 * calls inlined in open(), the widget passes the modal and a callbacks object.
 */
export function wireModalHandlers(
  modal: ModalElements,
  callbacks: ModalCallbacks,
): void {
  // Switch-camera button handler
  modal.switchCameraBtn.addEventListener('click', () => {
    callbacks.onSwitchCamera();
  });

  // Snapshot button handler
  modal.snapshotBtn.addEventListener('click', () => {
    callbacks.onSnapshot();
  });

  // Add-to-cart button handler — delegates entirely to the widget
  modal.addToCartBtn.addEventListener('click', () => {
    callbacks.onAddToCart();
  });

  // Mode toggle — Live / Photo
  modal.modeToggle.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const tab = target.closest('[data-mode]') as HTMLElement | null;
    if (!tab) return;
    const mode = tab.dataset['mode'] as 'live' | 'photo' | undefined;
    if (mode) {
      callbacks.onSwitchMode(mode);
    }
  });

  // Photo upload area — forward clicks to the hidden file input
  modal.photoUploadArea.addEventListener('click', () => {
    modal.photoInput.click();
  });
  modal.photoUploadArea.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      modal.photoInput.click();
    }
  });

  // File input change — load the selected photo
  modal.photoInput.addEventListener('change', () => {
    const file = modal.photoInput.files?.[0];
    if (file) callbacks.onPhotoFile(file);
  });

  // Drag-and-drop onto the upload area
  modal.photoUploadArea.addEventListener('dragover', (e) => {
    e.preventDefault();
    modal.photoUploadArea.classList.add('ft-photo-upload--drag-over');
  });
  modal.photoUploadArea.addEventListener('dragleave', () => {
    modal.photoUploadArea.classList.remove('ft-photo-upload--drag-over');
  });
  modal.photoUploadArea.addEventListener('drop', (e) => {
    e.preventDefault();
    modal.photoUploadArea.classList.remove('ft-photo-upload--drag-over');
    const file = e.dataTransfer?.files[0];
    if (file && file.type.startsWith('image/')) callbacks.onPhotoFile(file);
  });
}

// ---------------------------------------------------------------------------
// Cart toast UI
// ---------------------------------------------------------------------------

/**
 * Create a cart toast handler with its own timer state.
 *
 * Returns both the handler function and a dispose callback for cleanup.
 * The handler shows a transient toast inside the given container element.
 */
export function createCartToastHandler(): {
  show: (container: HTMLElement, type: 'success' | 'error', message: string) => void;
  dispose: () => void;
} {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const show = (container: HTMLElement, type: 'success' | 'error', message: string): void => {
    const TOAST_CLASS = 'ft-cart-toast';
    const VISIBLE_CLASS = 'ft-cart-toast--visible';
    const ERROR_CLASS = 'ft-cart-toast--error';

    let toast = container.querySelector<HTMLDivElement>(`.${TOAST_CLASS}`);
    if (!toast) {
      toast = document.createElement('div');
      toast.className = TOAST_CLASS;
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      // Inline styles keep the toast self-contained (no external CSS dependency)
      Object.assign(toast.style, {
        position: 'absolute',
        bottom: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '8px 16px',
        borderRadius: '4px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#fff',
        background: '#22c55e', // default: success green
        opacity: '0',
        transition: 'opacity 0.2s ease',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        zIndex: '10',
      });
      container.appendChild(toast);
    }

    // Update content + styling
    toast.textContent = message;
    if (type === 'error') {
      toast.style.background = '#ef4444';
      toast.classList.add(ERROR_CLASS);
    } else {
      toast.style.background = '#22c55e';
      toast.classList.remove(ERROR_CLASS);
    }

    // Show
    toast.style.opacity = '1';
    toast.classList.add(VISIBLE_CLASS);

    // Auto-dismiss (reset timer if re-triggered)
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      if (toast) {
        toast.style.opacity = '0';
        toast.classList.remove(VISIBLE_CLASS);
      }
      timer = null;
    }, 3000);
  };

  const dispose = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  return { show, dispose };
}

// ---------------------------------------------------------------------------
// Mode switching DOM logic
// ---------------------------------------------------------------------------

/** Callbacks for mode-switching side effects. */
export interface ModeSwitchCallbacks {
  /** Pause tracking loop and camera. */
  onPauseTracking: () => void;
  /** Resume camera stream and attach to video element. */
  onResumeCamera: (video: HTMLVideoElement) => Promise<void>;
  /** Restart the tracking loop. */
  onStartTracking: () => void;
}

/**
 * Switch the modal DOM between live camera mode and photo upload mode.
 *
 * Handles all visual state (tab active states, element visibility).
 * Calls the appropriate callbacks for non-DOM side effects (camera, tracking).
 */
export async function switchModeDOM(
  modal: ModalElements,
  mode: 'live' | 'photo',
  callbacks: ModeSwitchCallbacks,
): Promise<void> {
  const { modeToggle, photoUploadArea, photoImg, video, switchCameraBtn } = modal;

  // Update tab active state
  for (const tab of modeToggle.querySelectorAll<HTMLButtonElement>('[data-mode]')) {
    const isActive = tab.dataset['mode'] === mode;
    tab.classList.toggle('ft-mode-toggle__tab--active', isActive);
    tab.setAttribute('aria-selected', String(isActive));
  }

  if (mode === 'photo') {
    // Pause tracking loop and camera
    callbacks.onPauseTracking();

    // Hide live-only controls and show upload area
    video.style.display = 'none';
    switchCameraBtn.style.display = 'none';
    photoImg.style.display = 'none';
    photoUploadArea.style.display = 'flex';
  } else {
    // Restore live mode — hide photo/upload area
    photoUploadArea.style.display = 'none';
    photoImg.style.display = 'none';
    video.style.display = '';
    switchCameraBtn.style.display = '';

    // Clear any previously loaded photo from the input
    modal.photoInput.value = '';

    // Restart camera stream
    await callbacks.onResumeCamera(video);

    callbacks.onStartTracking();
  }
}

// ---------------------------------------------------------------------------
// Photo loading DOM logic
// ---------------------------------------------------------------------------

/** Callbacks for photo-loading side effects. */
export interface PhotoLoadCallbacks {
  /** Called with the loaded image and canvas dimensions after a successful load. */
  onPhotoLoaded: (img: HTMLImageElement, width: number, height: number) => void;
  /** Track the photo upload in analytics. */
  onTrackPhotoUploaded: () => void;
}

/**
 * Load an image File, show it in the modal, and invoke callbacks for
 * face detection and analytics.
 */
export function loadPhotoFile(
  modal: ModalElements,
  file: File,
  callbacks: PhotoLoadCallbacks,
): Promise<void> {
  return new Promise((resolve) => {
    const { photoUploadArea, photoImg, canvas } = modal;
    const url = URL.createObjectURL(file);

    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);

      // Show the photo, hide upload area
      photoImg.src = img.src;
      photoImg.style.display = '';
      photoUploadArea.style.display = 'none';

      // Size the canvas to match the image
      const containerRect = canvas.getBoundingClientRect();
      const w = containerRect.width > 0 ? containerRect.width : img.naturalWidth || 640;
      const h = containerRect.height > 0 ? containerRect.height : img.naturalHeight || 480;
      canvas.width = w;
      canvas.height = h;

      // Invoke callbacks for face detection and analytics
      callbacks.onPhotoLoaded(img, w, h);
      callbacks.onTrackPhotoUploaded();

      resolve();
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve();
    };
    img.src = url;
  });
}
